import { z } from "zod";
/**
 * NL Werkkostenregeling (WKR) — shared vocabulary + wire types for the
 * jurisdiction-exclusive nl_wkr subsystem (route apps/api/src/routes/accounting/nl/wkr.ts,
 * bridge table nl_wkr_entries). See docs/TAX.md "WKR (NL)".
 *
 * The category set is the fixed Dutch legal taxonomy — it mirrors the CHECK
 * constraint on nl_wkr_entries.category (migration 20260808100000). KEEP IN
 * SYNC with that CHECK; both sides carry this comment.
 */
export const NL_WKR_CATEGORIES = [
    "free_space", // vrije ruimte
    "intermediary", // intermediaire kosten
    "nil_valuation", // nihilwaardering
    "targeted_exemption", // gerichte vrijstelling
    "taxable_wage", // belast loon (boven de vrije ruimte)
];
export const nlWkrCategorySchema = z.enum(NL_WKR_CATEGORIES);
export const nlWkrSourceTypeSchema = z.enum([
    "expense",
    "ap_bill",
    "journal",
    "import_split",
]);
/** ?year= query param — only the API-only unreviewed endpoint still uses it. */
export const nlWkrYearQuerySchema = z.object({
    year: z.coerce.number().int().min(2000).max(2100),
});
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, {
    message: "must be YYYY-MM-DD",
})
    // Shape isn't enough: reject well-formed but impossible dates (2026-02-30)
    // at the boundary, where the caller gets the clean 400, instead of letting
    // Postgres throw "date/time field value out of range" a layer deeper. The
    // UTC round-trip catches V8's silent rollover (Date.parse('2026-02-30')
    // "succeeds" as Mar 2).
    .refine((s) => {
    const parsed = new Date(`${s}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === s;
}, { message: "must be a real calendar date" });
/**
 * ?from=&to= INCLUSIVE date range for totals / entries index — fed by the
 * app-standard DateRangeFilter on the WKR report.
 */
export const nlWkrRangeQuerySchema = z
    .object({ from: isoDate, to: isoDate })
    .refine((r) => r.from <= r.to, { message: "from must not be after to" });
/**
 * Duplicate sort_order values would fan out the sync helper's payload→line
 * join in nl_wkr_sync_from_lines (the SQL side RAISEs defensively; this
 * rejects the request before it reaches the RPC). Attach via
 * `.superRefine(assertUniqueSortOrders)` on line-item arrays.
 */
export const assertUniqueSortOrders = (items, ctx) => {
    const seen = new Set();
    for (const [index, item] of items.entries()) {
        if (seen.has(item.sort_order)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Duplicate sort_order ${item.sort_order}`,
                path: [index, "sort_order"],
            });
        }
        seen.add(item.sort_order);
    }
};
//# sourceMappingURL=nlWkr.js.map